/**
 * DailySheetPage — owner-only scanner for the Shakewallah daily ledger.
 *
 * See /app/backend/daily_sheet.py for the backend extraction pipeline.
 *
 * UX:
 *   1. Big "Take photo" / "Upload" affordance (mobile-first).
 *   2. Loading state while Claude extracts (~5-15s).
 *   3. Editable preview showing 5 sections:
 *        • Date + Cash In / Cash Out
 *        • Expenses (with auto-mapped category)
 *        • Absent staff (needs staff match)
 *        • Advances (needs staff match)
 *        • Vijay Ras In / Out (qty ledger)
 *        • Notes
 *   4. "Save Everything" fans out into Expenses / Staff / VijayRas.
 *
 * All buttons/inputs carry data-testid attributes for the testing agent.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Camera,
    Upload,
    Loader2,
    ChevronDown,
    ChevronRight,
    IndianRupee,
    Users,
    ShoppingBag,
    Milk,
    Trash2,
    Save,
    History as HistoryIcon,
    ScanText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { api } from '../lib/api';
import { VoucherPreviewCard } from '../components/VoucherPreviewCard';
// NOTE: We deliberately DO NOT import compressImageFile here.
// The daily-sheet endpoint runs its own server-side PIL preprocessing
// (EXIF-rotate + LANCZOS resize to 1600px + JPEG 85) which is materially
// sharper than the browser-canvas compression we use for bill uploads.
// Compressing on both ends softens handwritten pen strokes enough for
// Claude to miscount notes — e.g. reading 19 × ₹200 as 17 × ₹200.

/**
 * File → base64 (no data-uri prefix). Uses FileReader so it works even in
 * older PWA WebViews that don't have Blob.arrayBuffer support.
 */
function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const s = String(reader.result || '');
            const i = s.indexOf('base64,');
            resolve(i >= 0 ? s.slice(i + 7) : s);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const DENOMS = [500, 200, 100, 50, 20, 10];
const CATEGORY_OPTIONS = [
    { key: 'inventory', label: 'Inventory' },
    { key: 'beverages', label: 'Beverages' },
    { key: 'groceries', label: 'Groceries' },
    { key: 'local_purchases', label: 'Local Purchases' },
    { key: 'transport', label: 'Transport' },
    { key: 'marketing', label: 'Marketing' },
    { key: 'utilities', label: 'Utilities' },
    { key: 'salary', label: 'Salary' },
    { key: 'misc', label: 'Miscellaneous' },
];

/**
 * Small collapsible section wrapper for the preview panels.
 * Keeps the UI compact on mobile — user can expand only what they need.
 */
function Section({ title, icon: Icon, count, total, defaultOpen = true, children, testid }) {
    const [open, setOpen] = useState(defaultOpen);
    // Sprint 2026-08: `total` (₹) shown inline with the count badge so the
    // user can see the running total of a section without expanding it.
    // Recalculates live as the parent re-renders (via useMemo/state).
    const showTotal = typeof total === 'number' && !Number.isNaN(total);
    return (
        <Card className="p-0 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((x) => !x)}
                data-testid={testid ? `${testid}-toggle` : undefined}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
            >
                {open ? (
                    <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                ) : (
                    <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                )}
                {Icon && <Icon size={18} className="text-primary shrink-0" />}
                <span className="font-heading text-base flex-1">{title}</span>
                {showTotal && (
                    <span
                        className="font-mono text-sm tabular-nums text-foreground/90"
                        data-testid={testid ? `${testid}-total` : undefined}
                    >
                        ₹{Number(total).toLocaleString('en-IN')}
                    </span>
                )}
                {typeof count === 'number' && (
                    <Badge variant="secondary" data-testid={testid ? `${testid}-count` : undefined}>
                        {count}
                    </Badge>
                )}
            </button>
            {open && <div className="border-t border-border/60 p-4">{children}</div>}
        </Card>
    );
}

export default function DailySheetPage() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [phase, setPhase] = useState('idle'); // idle | scanning | preview | saving | saved
    // Phase 8 — set after a successful save so the VoucherPreviewCard can
    // load the preview from /daily-sheet/{id}/preview-vouchers.
    const [savedSheetId, setSavedSheetId] = useState(null);
    const [scanError, setScanError] = useState('');
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    // Extracted + user-edited state
    const [date, setDate] = useState('');
    const [cashIn, setCashIn] = useState({ d500: 0, d200: 0, d100: 0, d50: 0, d20: 0, d10: 0, coins: 0, total: 0 });
    const [cashOut, setCashOut] = useState({ d500: 0, d200: 0, d100: 0, d50: 0, d20: 0, d10: 0, coins: 0, total: 0 });
    const [vrbhandar, setVrbhandar] = useState([]); // Vijay Ras Bhandar IN
    const [vrOut, setVrOut] = useState([]); // Vijay Ras OUT
    const [expensesPlan, setExpensesPlan] = useState([]); // preview-plan[]
    const [absentPlan, setAbsentPlan] = useState([]);
    const [advancesPlan, setAdvancesPlan] = useState([]);
    const [dues, setDues] = useState([]);
    const [notes, setNotes] = useState('');

    // Sprint 2026-07 (fix): batch queue for multi-file uploads. Each file
    // becomes its OWN daily sheet — they are NOT merged. User reviews and
    // saves each in sequence. When one is saved (or skipped), the next
    // one is loaded automatically into the review pane.
    const [batchQueue, setBatchQueue] = useState([]);       // Array<extracted extraction from dsScan>
    const [batchIndex, setBatchIndex] = useState(0);        // Current position (0-indexed)
    // Sprint 2026-07 (edit-from-history): when the user opens a saved sheet
    // from the History page (Eye icon → /daily-sheet?open=<id>), we hydrate
    // the preview pane with the persisted values so they can review and
    // trigger a re-save. Tracks whether we're editing an existing record.
    const [editingSheetId, setEditingSheetId] = useState(null);

    // Staff list for the "Match staff" dropdowns.
    const [staffOptions, setStaffOptions] = useState([]);
    useEffect(() => {
        // Fire-and-forget: fetch staff for match-dropdowns. 403 (rare) is
        // fine — the field just falls back to "Unmatched".
        (async () => {
            try {
                const r = await (
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('billlens_token') || ''}` },
                    })
                ).json();
                if (Array.isArray(r?.items || r)) {
                    setStaffOptions((r.items || r).map((s) => ({ id: s.id, name: s.name })));
                }
            } catch {
                /* noop */
            }
        })();
    }, []);

    const MAX_SHEETS_PER_BATCH = 5;
    const openCamera = () => cameraRef.current?.click();
    const openGallery = () => fileRef.current?.click();

    // Helper: given ONE extracted daily-sheet payload from api.dsScan, hydrate
    // all the local state so it renders in the preview pane. Runs preview-plan
    // to map categories + staff.
    async function loadExtractionIntoPreview(extracted) {
        const plan = await api.dsPreviewPlan({
            date: extracted.date,
            expenses: extracted.expenses || [],
            absent: extracted.absent || [],
            advances: extracted.advances || [],
            dues: extracted.dues || [],
        });
        setDate(extracted.date || plan.date || new Date().toISOString().slice(0, 10));
        setCashIn(extracted.cash_in || { d500: 0, d200: 0, d100: 0, d50: 0, d20: 0, d10: 0, coins: 0, total: 0 });
        setCashOut(extracted.cash_out || { d500: 0, d200: 0, d100: 0, d50: 0, d20: 0, d10: 0, coins: 0, total: 0 });
        setVrbhandar(extracted.vijay_ras_bhandar || []);
        setVrOut(extracted.vijay_ras || []);
        // Phase 8 — enrich each expense row with `last_unit_price` from
        // item_master so the Unit Price input renders a tap-to-accept
        // placeholder ("₹53") whenever this category has been seen before.
        try {
            const { prices = {} } = await api.getItemLastPrices();
            const rows = (plan.expenses || []).map((r) => {
                const key = (r.name || '').toLowerCase().trim();
                return { ...r, last_unit_price: prices[key] ?? null };
            });
            setExpensesPlan(rows);
        } catch {
            setExpensesPlan(plan.expenses || []);
        }
        setAbsentPlan(plan.absent || []);
        setAdvancesPlan(plan.advances || []);
        setDues(plan.dues || []);
        setNotes(extracted.notes || '');
        setPhase('preview');
    }

    // Sprint 2026-07 (edit-from-history): if the URL has ?open=<sheet_id>,
    // fetch that sheet from the backend and hydrate the preview so the user
    // can review + edit + re-save without re-scanning the photo.
    //
    // Sprint 2026-08 fix (from owner report): when re-viewing a saved
    // sheet, the "Convert to Vouchers" card must still be reachable so
    // the user can regenerate/adjust vouchers. Previously we set
    // `phase = 'preview'` here, which hid the voucher card entirely
    // (that card only renders when `phase === 'saved' && savedSheetId`).
    // Now we set `savedSheetId` and force phase → 'saved' so the card
    // shows up for existing sheets exactly like it does after first
    // save.
    useEffect(() => {
        const sheetId = searchParams.get('open');
        if (!sheetId) return;
        let cancelled = false;
        (async () => {
            try {
                setPhase('scanning');
                const doc = await api.dsDetail(sheetId);
                if (cancelled) return;
                setEditingSheetId(sheetId);
                await loadExtractionIntoPreview(doc);
                if (cancelled) return;
                // Mark the sheet as already-saved so the voucher card renders
                // straight away (see fix note above).
                setSavedSheetId(sheetId);
                setPhase('saved');
                toast.info(`Editing sheet from ${doc.date || 'unknown date'}. Save updates existing record.`);
            } catch (e) {
                if (cancelled) return;
                toast.error(e?.response?.data?.detail || 'Could not open this sheet.');
                setPhase('idle');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onFileChosen = async (e) => {
        const rawFiles = Array.from(e.target.files || []);
        if (!rawFiles.length) return;
        e.target.value = ''; // allow re-selecting the same file(s)

        // Sprint 2026-07 fix: multi-file upload = MULTIPLE SEPARATE daily
        // sheets. We do NOT merge them (that was the previous bug — user
        // uploaded 5 different days and saw them combined into one sheet).
        // Instead, OCR all N in parallel, queue the results, and let the
        // user review + save each in sequence. Set MAX to 5 for sanity.
        const files = rawFiles.slice(0, MAX_SHEETS_PER_BATCH);
        if (rawFiles.length > MAX_SHEETS_PER_BATCH) {
            toast.warning(
                `Only the first ${MAX_SHEETS_PER_BATCH} sheets will be processed (you selected ${rawFiles.length}).`,
            );
        }

        setScanError('');
        setPhase('scanning');
        try {
            const b64s = await Promise.all(files.map((f) => fileToBase64(f)));
            const scanResps = await Promise.all(b64s.map((b) => api.dsScan(b)));

            // Collect only the successful extractions. Failed ones show up
            // in the toast so the user knows to retake those specific files.
            const good = [];
            let failCount = 0;
            scanResps.forEach((r) => {
                if (r?.ok && r.extracted) good.push(r.extracted);
                else failCount++;
            });
            if (!good.length) {
                setScanError('Sorry, none of the sheets could be read. Please retake in good light.');
                setPhase('idle');
                return;
            }
            if (failCount > 0) {
                toast.warning(`${failCount} of ${files.length} sheets couldn't be read — those will not appear.`);
            }

            // Queue all successful extractions; open the first for review.
            // The remaining wait in `batchQueue`; when the user saves or
            // skips the current one, we auto-advance to the next.
            setBatchQueue(good);
            setBatchIndex(0);
            await loadExtractionIntoPreview(good[0]);
            if (good.length > 1) {
                toast.success(`${good.length} sheets scanned. Reviewing sheet 1 of ${good.length}.`);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message || 'Scan failed';
            setScanError(msg);
            setPhase('idle');
        }
    };

    /* -- Derived: totals for the "at a glance" strip at top of preview -- */
    const stats = useMemo(() => {
        const expensesTotal = expensesPlan
            .filter((e) => e.include !== false)
            .reduce((a, b) => a + (Number(b.amount) || 0), 0);
        const advancesTotal = advancesPlan.reduce((a, b) => a + (Number(b.amount) || 0), 0);
        const cashInTotal = Number(cashIn.total || 0);
        const cashOutTotal = Number(cashOut.total || 0);
        // Net Cash (owner formula, 2026-07):
        //   (Cash Out + Expenses + Advances) − Cash In
        // Positive → total outflows exceeded inflows for the day (cash deficit).
        // Negative → inflows exceeded outflows (cash surplus).
        return {
            expensesTotal,
            advancesTotal,
            cashInTotal,
            cashOutTotal,
            netCash: (cashOutTotal + expensesTotal + advancesTotal) - cashInTotal,
            absentCount: absentPlan.length,
        };
    }, [expensesPlan, advancesPlan, cashIn, cashOut, absentPlan]);

    const cashInputCell = (section, key, setter, testidPrefix) => (
        <Input
            type="number"
            inputMode="numeric"
            value={section[key] ?? 0}
            onChange={(e) => setter({ ...section, [key]: Number(e.target.value || 0) })}
            className="h-8 text-sm font-mono text-right"
            data-testid={`${testidPrefix}-${key}`}
        />
    );

    // Sprint 2026-07 fix: `window.confirm` (previously used here) is quietly
    // blocked by many mobile browsers — the "Save everything" tap silently
    // did nothing. Track the unmatched-staff confirmation in state and
    // render an AlertDialog instead, which works everywhere.
    //
    // Sprint 2026-08 v2: dialog now offers an inline "Create Staff" flow
    // (per owner spec) rather than a "Skip / Save anyway" bail-out. For
    // each unmatched OCR name, the user fills in salary + optional alias +
    // working days, hits Create, and we auto-match those staff back into
    // the sheet — preserving all in-progress edits. New staff also
    // retro-backfill their attendance for all previously saved sheets in
    // the same month (see backend `create_staff?backfill_month=YYYY-MM`).
    const [unmatchedDialog, setUnmatchedDialog] = useState({
        open: false,
        names: [],
        effectiveDate: null,
        forms: {},   // { [name]: { salary, alias, workingDays } }
        creating: false,
    });

    // Frontend-side name normalizer that matches the backend's `_norm_name`
    // — strips punctuation/spaces and lowercases. Used to re-link an OCR
    // row to a freshly-created staff record after the dialog closes.
    const _normName = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

    // The actual save routine — called both directly (no unmatched staff) and
    // from the AlertDialog "Save anyway" action.
    // Accepts `effectiveDate` explicitly so the caller can pass a
    // just-computed fallback date (React state updates are async, so the
    // component's `date` state may still be stale at this point).
    const performSave = async (effectiveDate = date) => {
        setPhase('saving');
        try {
            const resp = await api.dsSave({
                date: effectiveDate,
                cash_in: cashIn,
                cash_out: cashOut,
                vijay_ras_bhandar: vrbhandar,
                vijay_ras: vrOut,
                expenses: expensesPlan,
                absent: absentPlan,
                advances: advancesPlan,
                dues,
                notes,
            });
            const s = resp?.stats || {};
            toast.success(
                `Saved: ${s.expenses_inserted || 0} expenses · ${s.advances_inserted || 0} advances · ${s.absent_marked || 0} absent · Vijay Ras ${s.vijay_ras_saved ? '✓' : '—'}`
            );
            // Sprint 2026-08 · re-save cleanup toast. Tells the owner
            // exactly how many stale rows we scrubbed so they know a
            // fresh edit really replaced the old duplicated entries.
            const scrub = resp?.resave_scrub || {};
            const scrubBits = [];
            if (scrub.expenses)      scrubBits.push(`${scrub.expenses} old expense(s)`);
            if (scrub.advances)      scrubBits.push(`${scrub.advances} old advance(s)`);
            if (scrub.vijay_ras)     scrubBits.push('old Vijay Ras entry');
            if (scrub.vouchers)      scrubBits.push(`${scrub.vouchers} old voucher(s)`);
            if (scrub.stock_ledger)  scrubBits.push(`${scrub.stock_ledger} old stock row(s)`);
            if (scrubBits.length) {
                toast.info(`Replaced: ${scrubBits.join(' · ')}`, { duration: 6000 });
            }
            if ((s.warnings || []).length) {
                toast.warning(s.warnings.join(' · '), { duration: 8000 });
            }
            setPhase('saved');
            // Phase 8 — Remember the saved sheet id so the VoucherPreviewCard
            // can show up right after Save and drive the accounting-voucher
            // conversion flow.
            if (resp?.sheet_id) setSavedSheetId(resp.sheet_id);
            // Sprint 2026-07 fix: if there are more sheets waiting in the
            // batch queue, auto-advance to the next one after a short beat.
            if (batchQueue.length > 1 && batchIndex + 1 < batchQueue.length) {
                setTimeout(async () => {
                    const nextIdx = batchIndex + 1;
                    setBatchIndex(nextIdx);
                    await loadExtractionIntoPreview(batchQueue[nextIdx]);
                    toast.info(`Now reviewing sheet ${nextIdx + 1} of ${batchQueue.length}.`);
                }, 800);
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || 'Save failed';
            toast.error(msg);
            setPhase('preview');
        }
    };

    const handleSave = async () => {
        // Defensive: if the OCR failed to detect a date OR extracted a bad
        // year, fall back to today so the Save button never becomes a dead
        // end. The user can always override before clicking.
        let effectiveDate = date;
        if (!effectiveDate || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
            const today = new Date().toISOString().slice(0, 10);
            setDate(today);
            effectiveDate = today;
            toast.info(`No valid date detected — defaulting to today (${today}). Change it above if that's wrong.`);
        }
        const unmatchedNames = Array.from(new Set([
            ...absentPlan.filter((r) => !r.matched_staff_id).map((r) => r.name),
            ...advancesPlan.filter((r) => !r.matched_staff_id).map((r) => r.name),
        ].filter(Boolean)));
        if (unmatchedNames.length > 0) {
            // Seed a form for each unique unmatched name so the user only
            // fills salary once per person (not once per absent/advance row).
            const forms = {};
            unmatchedNames.forEach((n) => {
                forms[n] = { salary: '', alias: '', workingDays: '30' };
            });
            setUnmatchedDialog({
                open: true,
                names: unmatchedNames,
                effectiveDate,
                forms,
                creating: false,
            });
            return;
        }
        await performSave(effectiveDate);
    };

    // Sprint 2026-08 v2 — create staff inline from the unmatched dialog.
    // For each unmatched OCR name, POST /api/staff with backfill_month set
    // to the current sheet's month, then splice the returned staff_id into
    // the corresponding absent/advance rows so Save proceeds cleanly.
    const handleCreateUnmatchedStaff = async () => {
        const { names, forms, effectiveDate } = unmatchedDialog;
        // Validation — every row must have a positive salary.
        for (const n of names) {
            const f = forms[n] || {};
            const sal = Number(f.salary || 0);
            if (!Number.isFinite(sal) || sal <= 0) {
                toast.error(`Enter a valid monthly salary for "${n}".`);
                return;
            }
            const wd = Number(f.workingDays || 30);
            if (!Number.isFinite(wd) || wd <= 0 || wd > 31) {
                toast.error(`Working days for "${n}" must be between 1 and 31.`);
                return;
            }
        }
        setUnmatchedDialog((p) => ({ ...p, creating: true }));
        const backfillMonth = (effectiveDate || '').slice(0, 7);  // YYYY-MM
        const createdMap = {};  // { normalizedName: {id, name} }
        try {
            for (const n of names) {
                const f = forms[n];
                const saved = await api.createStaff(
                    {
                        name: n.trim(),
                        alias: (f.alias || '').trim() || null,
                        monthly_salary: Number(f.salary),
                        working_days: Number(f.workingDays || 30),
                    },
                    backfillMonth ? { backfill_month: backfillMonth } : {},
                );
                createdMap[_normName(saved.name)] = { id: saved.id, name: saved.name };
                // Also register any alias so subsequent rows using the alias
                // (unlikely in a single dialog but safe) match cleanly.
                if (saved.alias) {
                    createdMap[_normName(saved.alias)] = { id: saved.id, name: saved.name };
                }
            }
            // Splice new staff_ids into all unmatched rows.
            setAbsentPlan((prev) => prev.map((r) => {
                if (r.matched_staff_id) return r;
                const hit = createdMap[_normName(r.name)];
                return hit ? { ...r, matched_staff_id: hit.id, matched_staff_name: hit.name } : r;
            }));
            setAdvancesPlan((prev) => prev.map((r) => {
                if (r.matched_staff_id) return r;
                const hit = createdMap[_normName(r.name)];
                return hit ? { ...r, matched_staff_id: hit.id, matched_staff_name: hit.name } : r;
            }));
            // Refresh the global staff options list so the dropdowns pick
            // up the new records too.
            try {
                const r = await (
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('billlens_token') || ''}` },
                    })
                ).json();
                if (Array.isArray(r?.items || r)) {
                    setStaffOptions((r.items || r).map((s) => ({ id: s.id, name: s.name })));
                }
            } catch { /* noop */ }
            toast.success(
                `Added ${Object.keys(createdMap).length} staff. Attendance backfilled for ${backfillMonth}. Review the sheet and hit Save.`,
                { duration: 5000 },
            );
            setUnmatchedDialog({ open: false, names: [], effectiveDate: null, forms: {}, creating: false });
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || 'Failed to create staff.');
            setUnmatchedDialog((p) => ({ ...p, creating: false }));
        }
    };

    const reset = () => {
        setPhase('idle');
        setScanError('');
        setDate('');
        setExpensesPlan([]);
        setAbsentPlan([]);
        setAdvancesPlan([]);
        setDues([]);
        setNotes('');
        setVrbhandar([]);
        setVrOut([]);
        // Also clear the batch queue — user is starting fresh.
        setBatchQueue([]);
        setBatchIndex(0);
    };

    // Sprint 2026-07 fix: skip current sheet (do NOT save) and jump to
    // the next in the batch. Useful when a scan was garbage and the user
    // wants to move on without editing it.
    const skipToNextInBatch = async () => {
        if (batchQueue.length <= 1 || batchIndex + 1 >= batchQueue.length) {
            toast.info('No more sheets in this batch.');
            reset();
            return;
        }
        const nextIdx = batchIndex + 1;
        setBatchIndex(nextIdx);
        await loadExtractionIntoPreview(batchQueue[nextIdx]);
        toast.info(`Skipped. Now reviewing sheet ${nextIdx + 1} of ${batchQueue.length}.`);
    };

    return (
        <div data-testid="daily-sheet-page" className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-heading text-2xl flex items-center gap-2">
                        <ScanText size={22} className="text-primary" /> Daily Sheet Scanner
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Snap the day's handwritten ledger — Expenses, Staff, Vijay Ras and Cash all filed in one tap.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => nav('/daily-sheet/history')}
                    data-testid="ds-open-history"
                >
                    <HistoryIcon size={16} className="mr-2" /> History
                </Button>
            </div>

            {phase === 'idle' && (
                <Card className="p-6 border-dashed border-2 border-border/70">
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                            <ScanText size={30} className="text-primary" />
                        </div>
                        <div>
                            <h3 className="font-heading text-lg mb-1">Upload today's sheet</h3>
                            <p className="text-sm text-muted-foreground">
                                Take a photo, pick from gallery, or upload a PDF/image.
                                For multiple days, select up to <b>5 sheets at once</b> — we'll queue them
                                and you review + save each separately.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                            <Button
                                onClick={openCamera}
                                data-testid="ds-camera-btn"
                                className="min-w-[180px]"
                            >
                                <Camera size={18} className="mr-2" /> Take photo
                            </Button>
                            <Button
                                variant="outline"
                                onClick={openGallery}
                                data-testid="ds-upload-btn"
                                className="min-w-[180px]"
                            >
                                <Upload size={18} className="mr-2" /> Upload photo / PDF
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground/80 pt-1">
                            Supported: JPG, PNG, HEIC, WEBP, PDF · Up to 5 pages · Max 10 MB each
                        </p>
                        {scanError && (
                            <p className="text-sm text-destructive pt-2" data-testid="ds-scan-error">
                                {scanError}
                            </p>
                        )}
                        <input
                            ref={cameraRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={onFileChosen}
                        />
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                            className="hidden"
                            onChange={onFileChosen}
                        />
                    </div>
                </Card>
            )}

            {phase === 'scanning' && (
                <Card className="p-10 text-center" data-testid="ds-scanning">
                    <Loader2 size={30} className="mx-auto animate-spin text-primary mb-3" />
                    <div className="font-heading text-lg">Reading your sheet…</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        This takes about 10 seconds. Please stay on this page.
                    </p>
                </Card>
            )}

            {(phase === 'preview' || phase === 'saving' || phase === 'saved') && (
                <>
                    {/* At-a-glance strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card className="p-3">
                            <div className="text-xs text-muted-foreground">Expenses</div>
                            <div className="text-lg font-mono font-medium" data-testid="ds-total-expenses">
                                ₹{stats.expensesTotal.toLocaleString('en-IN')}
                            </div>
                        </Card>
                        <Card className="p-3">
                            <div className="text-xs text-muted-foreground">Advances</div>
                            <div className="text-lg font-mono font-medium" data-testid="ds-total-advances">
                                ₹{stats.advancesTotal.toLocaleString('en-IN')}
                            </div>
                        </Card>
                        <Card className="p-3">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                Net cash
                                <span
                                    className="text-[10px] text-muted-foreground/70"
                                    title="Net Cash = (Cash Out + Expenses + Advances) − Cash In"
                                >
                                    ⓘ
                                </span>
                            </div>
                            <div
                                className={`text-lg font-mono font-medium ${
                                    stats.netCash > 0
                                        ? 'text-amber-700'
                                        : stats.netCash < 0
                                        ? 'text-green-700'
                                        : 'text-foreground'
                                }`}
                                data-testid="ds-total-net-cash"
                            >
                                ₹{stats.netCash.toLocaleString('en-IN')}
                            </div>
                            <div className="text-[10px] text-muted-foreground/70 mt-0.5 font-mono">
                                (out + exp + adv) − in
                            </div>
                        </Card>
                        <Card className="p-3">
                            <div className="text-xs text-muted-foreground">Absent</div>
                            <div className="text-lg font-mono font-medium">{stats.absentCount}</div>
                        </Card>
                    </div>

                    {/* Date */}
                    <Card className="p-4">
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Sheet date <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="mt-1 max-w-xs"
                            data-testid="ds-date-input"
                        />
                        <p className="text-[10px] text-muted-foreground leading-tight mt-1">
                            Format: YYYY-MM-DD · we'll default to today if you leave it blank
                        </p>
                    </Card>

                    {/* Cash In / Cash Out */}
                    <Section title="Cash In / Cash Out" icon={IndianRupee} testid="ds-section-cash">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { label: 'Cash In', section: cashIn, setter: setCashIn, prefix: 'ds-cashin' },
                                { label: 'Cash Out', section: cashOut, setter: setCashOut, prefix: 'ds-cashout' },
                            ].map((col) => {
                                const recomputedTotal =
                                    DENOMS.reduce((a, d) => a + d * (col.section[`d${d}`] || 0), 0) +
                                    Number(col.section.coins || 0);
                                return (
                                    <div key={col.label}>
                                        <div className="font-heading text-sm mb-2">{col.label}</div>
                                        <div className="grid grid-cols-3 gap-2 items-center text-xs">
                                            <div className="col-span-1 text-muted-foreground">₹</div>
                                            <div className="col-span-1 text-muted-foreground text-right">Notes</div>
                                            <div className="col-span-1 text-muted-foreground text-right">Value</div>
                                            {DENOMS.map((d) => (
                                                <div key={d} className="contents">
                                                    <div className="font-mono">₹{d}</div>
                                                    {cashInputCell(col.section, `d${d}`, col.setter, col.prefix)}
                                                    <div className="text-right font-mono text-muted-foreground">
                                                        ₹{(d * (col.section[`d${d}`] || 0)).toLocaleString('en-IN')}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="font-mono">Coins</div>
                                            {cashInputCell(col.section, 'coins', col.setter, col.prefix)}
                                            <div />
                                            <div className="font-heading text-sm pt-2">Total</div>
                                            {cashInputCell(col.section, 'total', col.setter, col.prefix)}
                                            <div className="text-right font-mono text-muted-foreground pt-2">
                                                {recomputedTotal !== Number(col.section.total || 0) && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            col.setter({ ...col.section, total: recomputedTotal })
                                                        }
                                                        className="text-primary hover:underline"
                                                        data-testid={`${col.prefix}-autocalc`}
                                                    >
                                                        Auto ₹{recomputedTotal.toLocaleString('en-IN')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Section>

                    {/* Expenses */}
                    <Section
                        title="Expenses"
                        icon={ShoppingBag}
                        count={expensesPlan.filter((e) => e.include !== false).length}
                        total={stats.expensesTotal}
                        testid="ds-section-expenses"
                    >
                        <div className="space-y-2">
                            {expensesPlan.length === 0 && (
                                <div className="text-sm text-muted-foreground py-2 text-center">
                                    No expenses detected.
                                </div>
                            )}
                            {expensesPlan.map((row, idx) => (
                                <div
                                    key={`${row.name}-${idx}`}
                                    className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-border/40 last:border-0"
                                    data-testid={`ds-expense-row-${idx}`}
                                >
                                    <div className="col-span-1">
                                        <Checkbox
                                            checked={row.include !== false}
                                            onCheckedChange={(v) => {
                                                const copy = [...expensesPlan];
                                                copy[idx] = { ...copy[idx], include: !!v };
                                                setExpensesPlan(copy);
                                            }}
                                            data-testid={`ds-expense-include-${idx}`}
                                        />
                                    </div>
                                    <div className="col-span-3 text-sm font-medium truncate">{row.name}</div>
                                    <div className="col-span-3">
                                        <Select
                                            value={row.category_key}
                                            onValueChange={(v) => {
                                                const opt = CATEGORY_OPTIONS.find((o) => o.key === v);
                                                const copy = [...expensesPlan];
                                                copy[idx] = {
                                                    ...copy[idx],
                                                    category_key: v,
                                                    category_label: opt ? opt.label : v,
                                                };
                                                setExpensesPlan(copy);
                                            }}
                                        >
                                            <SelectTrigger
                                                className="h-8 text-xs"
                                                data-testid={`ds-expense-category-${idx}`}
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CATEGORY_OPTIONS.map((o) => (
                                                    <SelectItem key={o.key} value={o.key}>
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-2">
                                        {/* Phase 8 — Unit price for item rows.
                                            Auto-derived qty = amount / unit_price feeds
                                            the stock ledger when this voucher is committed. */}
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                step="0.01"
                                                placeholder={
                                                    row.last_unit_price
                                                        ? `₹${row.last_unit_price}`
                                                        : 'Unit ₹'
                                                }
                                                value={row.unit_price ?? ''}
                                                onChange={(e) => {
                                                    const copy = [...expensesPlan];
                                                    copy[idx] = {
                                                        ...copy[idx],
                                                        unit_price: e.target.value === '' ? null : Number(e.target.value),
                                                    };
                                                    setExpensesPlan(copy);
                                                }}
                                                title={
                                                    row.last_unit_price
                                                        ? `Tap to reuse last price ₹${row.last_unit_price}`
                                                        : 'Optional — enter unit price to auto-track stock'
                                                }
                                                onFocus={(e) => {
                                                    // Tap-to-accept: if blank + we have a last price, fill it.
                                                    if (!row.unit_price && row.last_unit_price) {
                                                        const copy = [...expensesPlan];
                                                        copy[idx] = { ...copy[idx], unit_price: row.last_unit_price };
                                                        setExpensesPlan(copy);
                                                    }
                                                }}
                                                className="h-8 text-xs text-right font-mono"
                                                data-testid={`ds-expense-unit-price-${idx}`}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-3">
                                        <Input
                                            type="number"
                                            inputMode="decimal"
                                            value={row.amount ?? 0}
                                            onChange={(e) => {
                                                const copy = [...expensesPlan];
                                                copy[idx] = {
                                                    ...copy[idx],
                                                    amount: Number(e.target.value || 0),
                                                };
                                                setExpensesPlan(copy);
                                            }}
                                            className="h-8 text-sm text-right font-mono"
                                            data-testid={`ds-expense-amount-${idx}`}
                                        />
                                        {/* Auto-derived qty hint */}
                                        {row.unit_price && Number(row.unit_price) > 0 && Number(row.amount) > 0 && (
                                            <div className="text-[10px] text-muted-foreground text-right mt-0.5 font-mono">
                                                = {(Number(row.amount) / Number(row.unit_price)).toFixed(2)} units
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Absent staff */}
                    <Section
                        title="Absent staff"
                        icon={Users}
                        count={absentPlan.length}
                        testid="ds-section-absent"
                        defaultOpen={absentPlan.length > 0}
                    >
                        {absentPlan.length === 0 && (
                            <div className="text-sm text-muted-foreground py-2 text-center">
                                No absences detected.
                            </div>
                        )}
                        {absentPlan.map((row, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center py-1.5" data-testid={`ds-absent-row-${idx}`}>
                                <div className="col-span-4 text-sm">{row.name}</div>
                                <div className="col-span-6">
                                    <Select
                                        value={row.matched_staff_id || 'none'}
                                        onValueChange={(v) => {
                                            const copy = [...absentPlan];
                                            const staff = staffOptions.find((s) => s.id === v);
                                            copy[idx] = {
                                                ...copy[idx],
                                                matched_staff_id: v === 'none' ? null : v,
                                                matched_staff_name: staff?.name || null,
                                            };
                                            setAbsentPlan(copy);
                                        }}
                                    >
                                        <SelectTrigger className="h-8 text-xs" data-testid={`ds-absent-match-${idx}`}>
                                            <SelectValue placeholder="Match to staff…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— Skip (unmatched) —</SelectItem>
                                            {staffOptions.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>
                                                    {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2 text-right">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setAbsentPlan(absentPlan.filter((_, i) => i !== idx))}
                                        data-testid={`ds-absent-remove-${idx}`}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Section>

                    {/* Advances */}
                    <Section
                        title="Staff advances"
                        icon={IndianRupee}
                        count={advancesPlan.length}
                        total={stats.advancesTotal}
                        testid="ds-section-advances"
                        defaultOpen={advancesPlan.length > 0}
                    >
                        {advancesPlan.length === 0 && (
                            <div className="text-sm text-muted-foreground py-2 text-center">
                                No advances detected.
                            </div>
                        )}
                        {advancesPlan.map((row, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center py-1.5" data-testid={`ds-advance-row-${idx}`}>
                                <div className="col-span-3 text-sm">{row.name}</div>
                                <div className="col-span-5">
                                    <Select
                                        value={row.matched_staff_id || 'none'}
                                        onValueChange={(v) => {
                                            const copy = [...advancesPlan];
                                            const staff = staffOptions.find((s) => s.id === v);
                                            copy[idx] = {
                                                ...copy[idx],
                                                matched_staff_id: v === 'none' ? null : v,
                                                matched_staff_name: staff?.name || null,
                                            };
                                            setAdvancesPlan(copy);
                                        }}
                                    >
                                        <SelectTrigger className="h-8 text-xs" data-testid={`ds-advance-match-${idx}`}>
                                            <SelectValue placeholder="Match to staff…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— Skip (unmatched) —</SelectItem>
                                            {staffOptions.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>
                                                    {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-3">
                                    <Input
                                        type="number"
                                        inputMode="decimal"
                                        value={row.amount ?? 0}
                                        onChange={(e) => {
                                            const copy = [...advancesPlan];
                                            copy[idx] = { ...copy[idx], amount: Number(e.target.value || 0) };
                                            setAdvancesPlan(copy);
                                        }}
                                        className="h-8 text-sm text-right font-mono"
                                        data-testid={`ds-advance-amount-${idx}`}
                                    />
                                </div>
                                <div className="col-span-1 text-right">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setAdvancesPlan(advancesPlan.filter((_, i) => i !== idx))}
                                        data-testid={`ds-advance-remove-${idx}`}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Section>

                    {/* Vijay Ras */}
                    <Section
                        title="Vijay Ras (father's store)"
                        icon={Milk}
                        count={
                            vrbhandar.filter((r) => Number(r.amount) > 0).length +
                            vrOut.filter((r) => Number(r.amount) > 0).length
                        }
                        testid="ds-section-vijay-ras"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { label: 'IN (bought from them)', list: vrbhandar, setter: setVrbhandar, prefix: 'vrbhandar' },
                                { label: 'OUT (sold to them)', list: vrOut, setter: setVrOut, prefix: 'vrout' },
                            ].map((col) => (
                                <div key={col.label}>
                                    <div className="font-heading text-sm mb-2">{col.label}</div>
                                    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                                        {col.list.map((row, i) => (
                                            <div key={`${row.name}-${i}`} className="grid grid-cols-6 gap-2 items-center">
                                                <div className="col-span-4 text-xs font-medium truncate">{row.name}</div>
                                                <div className="col-span-2">
                                                    <Input
                                                        type="number"
                                                        inputMode="decimal"
                                                        value={row.amount ?? 0}
                                                        onChange={(e) => {
                                                            const copy = [...col.list];
                                                            copy[i] = { ...copy[i], amount: Number(e.target.value || 0) };
                                                            col.setter(copy);
                                                        }}
                                                        className="h-7 text-xs text-right font-mono"
                                                        data-testid={`ds-${col.prefix}-qty-${i}`}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">
                            Prices are added at month-end from the Vijay Ras page for settlement.
                        </p>
                    </Section>

                    {/* Dues */}
                    {dues.length > 0 && (
                        <Section title="Customer dues (noted only)" defaultOpen>
                            <p className="text-xs text-muted-foreground mb-2">
                                We're capturing these on the daily sheet. To post them to a customer's ledger, use
                                the Invoicing → Customers page manually for now.
                            </p>
                            {dues.map((d, i) => (
                                <div key={i} className="text-sm flex justify-between py-1">
                                    <span>{d.name}</span>
                                    <span className="font-mono">₹{Number(d.amount).toLocaleString('en-IN')}</span>
                                </div>
                            ))}
                        </Section>
                    )}

                    {/* Notes */}
                    <Card className="p-4">
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Notes
                        </Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="mt-1"
                            data-testid="ds-notes-input"
                        />
                    </Card>

                    {/* Save bar */}
                    <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border/60 -mx-4 md:-mx-6 px-4 md:px-6 py-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
                        {/* Sprint 2026-07 fix: batch progress. Only visible
                            when more than one sheet was uploaded in a batch. */}
                        {batchQueue.length > 1 ? (
                            <div className="flex items-center gap-2 text-xs" data-testid="ds-batch-progress">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2 py-1 text-primary font-mono tabular-nums">
                                    Sheet {batchIndex + 1} of {batchQueue.length}
                                </span>
                                {batchIndex + 1 < batchQueue.length && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={skipToNextInBatch}
                                        data-testid="ds-batch-skip-btn"
                                        className="h-8 text-xs"
                                    >
                                        Skip this sheet →
                                    </Button>
                                )}
                            </div>
                        ) : <div />}
                        <div className="flex gap-2 items-center">
                            <Button variant="ghost" onClick={reset} data-testid="ds-reset-btn">
                                Discard &amp; scan again
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={phase === 'saving' || phase === 'saved'}
                                data-testid="ds-save-btn"
                            >
                                {phase === 'saving' ? (
                                    <>
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                        Saving…
                                    </>
                                ) : phase === 'saved' ? (
                                    'Saved ✓'
                                ) : (
                                    <>
                                        <Save size={16} className="mr-2" />
                                        {batchQueue.length > 1
                                            ? `Save sheet ${batchIndex + 1} of ${batchQueue.length}`
                                            : 'Save everything'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Phase 8 — Voucher preview appears just below the Save
                        row so it's the obvious next step (fresh saves) and
                        stays visible when the user re-opens a sheet from
                        the history page. Prior placement was at the top of
                        the page which was easy to miss on scroll. */}
                    {phase === 'saved' && savedSheetId && (
                        <div className="mt-2" data-testid="ds-voucher-slot">
                            <VoucherPreviewCard
                                sheetId={savedSheetId}
                                onCommitted={() => { /* toast handled inside */ }}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Unmatched-staff — inline "Create Staff" form (Sprint 2026-08 v2).
                Replaces the earlier "Skip / Save anyway" AlertDialog per
                owner spec: create the missing staff on the spot, keep all
                sheet edits intact, and retro-backfill attendance for prior
                sheets in the same month via `?backfill_month=`. */}
            <Dialog
                open={unmatchedDialog.open}
                onOpenChange={(v) => {
                    if (unmatchedDialog.creating) return;   // don't allow escape mid-save
                    setUnmatchedDialog((prev) => ({ ...prev, open: v }));
                }}
            >
                <DialogContent
                    className="max-w-lg max-h-[85vh] overflow-y-auto"
                    data-testid="ds-unmatched-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>Add missing staff</DialogTitle>
                        <DialogDescription>
                            These OCR names don't match any staff record yet. Fill in the
                            salary for each person — they'll be added instantly and
                            marked <strong>present</strong> on every previously saved sheet in{' '}
                            <span className="font-mono">
                                {(unmatchedDialog.effectiveDate || '').slice(0, 7) || 'this month'}
                            </span>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-1">
                        {unmatchedDialog.names.map((n) => {
                            const f = unmatchedDialog.forms[n] || { salary: '', alias: '', workingDays: '30' };
                            const updateForm = (key, val) => {
                                setUnmatchedDialog((prev) => ({
                                    ...prev,
                                    forms: { ...prev.forms, [n]: { ...prev.forms[n], [key]: val } },
                                }));
                            };
                            return (
                                <div
                                    key={n}
                                    className="rounded-md border border-border/70 bg-muted/20 p-3 space-y-2"
                                    data-testid={`ds-unmatched-row-${n}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-sm font-medium uppercase tracking-wide">
                                            {n}
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                            new staff
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-[11px] text-muted-foreground">
                                                Monthly salary (₹)
                                                <span className="text-destructive"> *</span>
                                            </Label>
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                min="0"
                                                value={f.salary}
                                                onChange={(e) => updateForm('salary', e.target.value)}
                                                placeholder="12000"
                                                className="h-8 text-sm font-mono"
                                                data-testid={`ds-unmatched-salary-${n}`}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-[11px] text-muted-foreground">
                                                Working days / month
                                            </Label>
                                            <Input
                                                type="number"
                                                inputMode="numeric"
                                                min="1"
                                                max="31"
                                                value={f.workingDays}
                                                onChange={(e) => updateForm('workingDays', e.target.value)}
                                                className="h-8 text-sm font-mono"
                                                data-testid={`ds-unmatched-workingdays-${n}`}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-[11px] text-muted-foreground">
                                            Alias / nickname (optional)
                                        </Label>
                                        <Input
                                            value={f.alias}
                                            onChange={(e) => updateForm('alias', e.target.value)}
                                            placeholder='e.g. "culter" for Amarnath'
                                            className="h-8 text-sm"
                                            data-testid={`ds-unmatched-alias-${n}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <DialogFooter className="pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                if (unmatchedDialog.creating) return;
                                setUnmatchedDialog({ open: false, names: [], effectiveDate: null, forms: {}, creating: false });
                            }}
                            data-testid="ds-unmatched-cancel"
                            disabled={unmatchedDialog.creating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateUnmatchedStaff}
                            data-testid="ds-unmatched-create"
                            disabled={unmatchedDialog.creating}
                        >
                            {unmatchedDialog.creating ? (
                                <><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</>
                            ) : (
                                <>Add {unmatchedDialog.names.length} staff &amp; return</>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
